import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import useOrganization from '~/components/OrganizationContext';
import { getLobDescription } from '~/Utils/lobUtils';

import type { ScopeValues } from './scopeFields';
import SCOPE_TEXTS from './texts';

/** One-line summaries of a scope's sub-org and LoB axes, for a list row. */
export const useScopeSummary = ({ is_all_sub_orgs, sub_organization_ids, is_all_lobs, lobs }: ScopeValues) => {
  const { subOrganizations, subOrganizationEnabled } = useOrganization() as {
    subOrganizations?: { id: number; name: string }[];
    subOrganizationEnabled?: boolean;
  };
  const { lobConfigurationsDict } = useLobConfiguration();

  const subOrgIds = sub_organization_ids ?? [];
  const lobKeys = lobs ?? [];
  const scopedSubOrgNames = (subOrganizationEnabled ? (subOrganizations ?? []) : [])
    .filter((subOrg) => subOrgIds.includes(subOrg.id))
    .map((subOrg) => subOrg.name)
    .join(', ');
  const scopedLobNames = lobKeys
    .map((lob) => getLobDescription(lob, lobConfigurationsDict))
    .filter(Boolean)
    .join(', ');
  // A specific scope whose members can't be resolved (a sub-org removed after scoping, a renamed LoB,
  // or the dicts not loaded yet) would render blank — indistinguishable from "no scope". Fall back to a
  // count so a specific-but-unresolvable scope still reads as scoped.
  const subOrgSummary = is_all_sub_orgs
    ? SCOPE_TEXTS.allSubOrgs
    : scopedSubOrgNames || `${subOrgIds.length} sub-org${subOrgIds.length === 1 ? '' : 's'}`;
  const lobSummary = is_all_lobs
    ? SCOPE_TEXTS.allLobs
    : scopedLobNames || `${lobKeys.length} LoB${lobKeys.length === 1 ? '' : 's'}`;

  return { subOrgSummary, lobSummary };
};
